import { useCallback, useEffect, useState } from "react";
import { usePlans } from "./PlanPicker";

/**
 * Provisionamento de tenants (epico 1/3) - só o operador da plataforma (MOC).
 * Cria a organização e convida o admin inicial por e-mail; nenhuma assinatura
 * no MOC Suite é exigida (billing é vínculo opcional, feito depois pelo
 * próprio admin da org em "Billing"). Mostrado na aba "Organizações" da
 * administração.
 */
interface OrgRow {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  ownerId: string | null;
  logoDataUrl: string | null; // logo cadastrada pelo admin; null = inicial do nome
  seats: { count: number; limit: number | null }; // null = ilimitado
  workspaceCount: number;
  billingLinked: boolean;
  planId: string | null;
  billingStatus: string | null;
  pendingCount: number;
  adminEmail: string | null;
  spentUsdThisMonth: number;
}

const STATUS_PT: Record<string, string> = {
  trialing: "em teste",
  active: "ativa",
  past_due: "pagamento pendente",
  canceled: "cancelada",
};

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return iso;
  }
}

interface Props {
  /** Chamado ao clicar "Gerenciar" - a página-pai troca o seletor de org e navega pra Visão geral. */
  onManageOrg: (orgId: string) => void;
}

export function OrganizationsPanel({ onManageOrg }: Props) {
  const plans = usePlans();
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLink, setLastLink] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/admin/organizations");
      if (r.ok) setOrgs((await r.json()).items ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    if (!name.trim() || !ownerEmail.trim()) return;
    setBusy(true);
    setError(null);
    setLastLink(null);
    try {
      const r = await fetch("/api/admin/organizations", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: name.trim(), ownerEmail: ownerEmail.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error ?? "Falha ao criar organização.");
        return;
      }
      setName("");
      setOwnerEmail("");
      if (!j.delivered) setLastLink(j.link); // sem webhook pra entregar: sysadmin copia
      await load();
    } finally {
      setBusy(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = orgs.filter(
    (o) => !q || o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q) || (o.adminEmail ?? "").toLowerCase().includes(q),
  );
  const linkedCount = orgs.filter((o) => o.billingLinked).length;
  const pendingTotal = orgs.reduce((acc, o) => acc + o.pendingCount, 0);
  const spendTotal = orgs.reduce((acc, o) => acc + o.spentUsdThisMonth, 0);

  return (
    <>
      <div className="admin-overview-section">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-value">{orgs.length}</div>
            <div className="stat-card-label">Organizações</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-value">{orgs.reduce((acc, o) => acc + o.seats.count, 0)}</div>
            <div className="stat-card-label">Contas no total</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-value">{linkedCount}</div>
            <div className="stat-card-label">Com billing vinculado</div>
            <div className="stat-card-hint">{orgs.length - linkedCount} nos limites locais</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-value">{pendingTotal}</div>
            <div className="stat-card-label">Aprovações pendentes</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-value">${spendTotal.toFixed(2)}</div>
            <div className="stat-card-label">Gasto de IA no mês (todas)</div>
          </div>
        </div>
      </div>

      <div className="admin-block">
        <h2>Criar organização</h2>
        <p className="admin-user-meta" style={{ marginBottom: 10 }}>
          Cria um tenant novo e convida o admin inicial por e-mail. Não exige plano no MOC Suite - o vínculo de
          billing é opcional e feito depois pelo próprio admin da org.
        </p>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="field" style={{ flex: "1 1 220px" }}>
            <span>Nome da organização</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Cliente Exemplo Ltda"
            />
          </label>
          <label className="field" style={{ flex: "1 1 220px" }}>
            <span>E-mail do admin inicial</span>
            <input
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="pessoa@cliente.com"
              spellCheck={false}
            />
          </label>
          <button className="btn-approve" disabled={busy} onClick={() => void create()} type="button">
            Criar organização
          </button>
        </div>

        {error && <div className="auth-error" style={{ marginTop: 10 }}>{error}</div>}
        {lastLink && (
          <div className="admin-user-meta" style={{ marginTop: 10, wordBreak: "break-all" }}>
            Sem webhook cadastrado pra essa pessoa - copie e envie o convite manualmente:{" "}
            <a href={lastLink}>{lastLink}</a>
          </div>
        )}
      </div>

      <div className="admin-block">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Todas as organizações ({filtered.length})</h2>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, slug ou admin…"
            style={{ maxWidth: 240, padding: "5px 8px", fontSize: 12.5 }}
          />
        </div>
        {loading ? (
          <p className="admin-user-meta">Carregando…</p>
        ) : filtered.length === 0 ? (
          <div className="admin-empty small">Nenhuma organização encontrada.</div>
        ) : (
          filtered.map((o) => (
            <div key={o.id} className="admin-user">
              {o.logoDataUrl ? (
                <img
                  src={o.logoDataUrl}
                  alt=""
                  style={{ width: 28, height: 28, borderRadius: 6, objectFit: "contain", background: "var(--bg-elevated)", flex: "0 0 auto" }}
                />
              ) : (
                <span
                  aria-hidden
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "var(--accent, #c15f3c)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13,
                    flex: "0 0 auto",
                  }}
                >
                  {o.name.charAt(0).toUpperCase()}
                </span>
              )}
              <div className="admin-user-info">
                <span className="admin-user-name">
                  {o.name}
                  {o.billingLinked ? (
                    <span className="badge-admin">{o.planId ?? "plano"} · {STATUS_PT[o.billingStatus ?? ""] ?? o.billingStatus}</span>
                  ) : (
                    <span className="badge-admin" style={{ background: "var(--bg-subtle)", color: "var(--text-faint)" }}>
                      limites locais
                    </span>
                  )}
                  {o.pendingCount > 0 && <span className="badge-admin">{o.pendingCount} pendente(s)</span>}
                </span>
                <span className="admin-user-email">{o.slug}{o.adminEmail ? ` · admin: ${o.adminEmail}` : ""}</span>
                <span className="admin-user-meta">
                  {o.seats.count}/{Number.isFinite(o.seats.limit) ? o.seats.limit : "∞"} assentos ·{" "}
                  {o.workspaceCount} workspace(s) · ${o.spentUsdThisMonth.toFixed(2)} no mês · criada {fmtDate(o.createdAt)}
                </span>
              </div>
              <div className="admin-user-actions" style={{ gap: 6 }}>
                {!o.billingLinked && (
                  <select
                    value={o.planId ?? ""}
                    title="Mover o plano desta organização (catálogo local)"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const planId = e.target.value;
                      if (!planId) return;
                      void fetch(`/api/admin/org/plan?orgId=${encodeURIComponent(o.id)}`, {
                        method: "PUT",
                        headers: { "content-type": "application/json" },
                        body: JSON.stringify({ planId }),
                      }).then(() => load());
                    }}
                    style={{ fontSize: 12 }}
                  >
                    <option value="">(sem plano)</option>
                    {plans.map((pl) => (
                      <option key={pl.id} value={pl.id}>{pl.name}</option>
                    ))}
                  </select>
                )}
                <button className="btn-reject" onClick={() => onManageOrg(o.id)} type="button">
                  Gerenciar
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
